import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useDict } from "@/lib/i18n/context";
import type { PublicTalentProfile } from "@/lib/types";

type TalentItem = PublicTalentProfile & { cv_url: string | null };

export function TalentPage() {
  const t = useDict();
  const [q, setQ] = useState("");
  const [items, setItems] = useState<TalentItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      const qs = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
      fetch(`/api/talent${qs}`, { credentials: "include" })
        .then(async (res) => {
          if (!res.ok) throw new Error("fail");
          return res.json() as Promise<{ items: TalentItem[] }>;
        })
        .then((data) => {
          setItems(data.items);
          setError(null);
        })
        .catch(() => setError(t.talent.loadError))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(timer);
  }, [q, t.talent.loadError]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t.talent.title}</h1>
        <p className="mt-1 text-muted">{t.talent.subtitle}</p>
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
        <Input
          className="ps-9"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.talent.search}
        />
      </div>

      {loading && <p className="text-muted">{t.reports.loading}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && !error && items && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border py-16 text-center">
          <Users className="mx-auto h-10 w-10 text-muted/50" />
          <p className="mt-3 font-medium">{t.talent.empty}</p>
          <p className="mt-1 text-sm text-muted">{t.talent.emptyDesc}</p>
        </div>
      )}

      {!loading && items && items.length > 0 && (
        <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
          {items.map((item) => (
            <li key={item.user_id}>
              <Link
                to={`/talent/${item.user_id}`}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 transition-colors hover:bg-surface/60"
              >
                <div className="min-w-0">
                  <p className="font-semibold">
                    {item.full_name || item.email}
                  </p>
                  <p className="truncate text-sm text-muted">{item.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge tone={item.has_cv ? "brand" : "neutral"}>
                    {item.has_cv ? t.talent.hasCv : t.talent.noCv}
                  </Badge>
                  <span className="text-sm font-medium text-brand-700 dark:text-brand-300">
                    {t.talent.view}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
